<template>
    <div>
        <Head :title="__('Reminder e-mail')" />

        <h1 class="mb-8 text-3xl font-bold">{{ __('Reminder e-mail') }}</h1>

        <div class="flex justify-between items-center">
            <div>
                <Link class="btn-cta" href="/reminders/create">
                    <span>{{ __('Create reminder') }}</span>
                </Link>
            </div>

            <div class="flex items-center justify-between">
                <SearchFilter
                    v-model="form.search"
                    class="mr-4 w-full min-w-[360px] max-w-md"
                    :hasDropdown="false"
                    @reset="reset"
                >
                </SearchFilter>
            </div>
        </div>

        <div class="mt-[25px] overflow-x-auto rounded-md bg-white shadow">
			<table class="w-full whitespace-nowrap relative">
				<tr class="text-left font-bold">
					<th class="px-6 pb-4 pt-6">Ämnesrad</th>
					<th class="px-6 pb-4 pt-6">{{ __('Status') }}</th>
					<th class="px-6 pb-4 pt-6">GCMA-godkänd</th>
                    <th class="px-6 pb-4 pt-6">{{ __('Type') }}</th>
					<th class="px-6 pb-4 pt-6">{{ __('Send date') }}</th>
				</tr>

				<tr
                    v-for="reminder in reminders.data"
                    :key="reminder.id"
                    class="focus-within:bg-gray-100 hover:bg-gray-100"
                >
                    <td class="px-6 py-4 border-t">
                        <Link
                            class="w-full contents"
                            :class="{'text-gray-400': !reminder.gcma_status}"
                            :href="`/reminders/${ reminder.id }/edit`"
                        >{{ reminder.subject }}</Link>
                    </td>
                    <td
                        class="px-6 py-4 border-t"
                    >
                        <Link
                            v-if=" reminder.number_of_recipients > 0"
                            class="w-full mb-4 underline flex gap-4 items-center"
                            :href="`/reminders/${ reminder.id }/statistics`">
                            Skickat till {{ reminder.number_of_recipients }} mottagare

                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <g clip-path="url(#clip0_935_601)">
                                    <path d="M21.2104 15.892C20.5742 17.3965 19.5792 18.7222 18.3123 19.7533C17.0454 20.7844 15.5452 21.4895 13.9428 21.8068C12.3405 22.1242 10.6848 22.0442 9.12055 21.5738C7.55627 21.1035 6.13103 20.2571 4.96942 19.1087C3.80782 17.9603 2.94522 16.5448 2.45704 14.986C1.96886 13.4272 1.86996 11.7725 2.169 10.1667C2.46804 8.56081 3.1559 7.05266 4.17245 5.77406C5.189 4.49546 6.50329 3.48535 8.0004 2.83203" stroke="currentColor" stroke-width="2.41667" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M22 12C22 10.6868 21.7413 9.38642 21.2388 8.17317C20.7362 6.95991 19.9997 5.85752 19.0711 4.92893C18.1425 4.00035 17.0401 3.26375 15.8268 2.7612C14.6136 2.25866 13.3132 2 12 2V12H22Z" stroke="currentColor" stroke-width="2.41667" stroke-linecap="round" stroke-linejoin="round"/>
                                </g>
                                <defs>
                                    <clipPath id="clip0_935_601">
                                        <rect width="24" height="24" fill="white"/>
                                    </clipPath>
                                </defs>
                            </svg>
                        </Link>
                        <span v-else class="w-full mb-4 block">
                            {{ capitalize(__(reminder.status)) }}
                        </span>
                        <div class="text-xs" v-html="getFilterCriteria(reminder)"></div>
                    </td>
                    <td class="px-6 py-4 border-t">
                        <span v-if="reminder.gcma_status">
                            <Icon name="check-circle" class="block h-6 w-6 fill-[#1d3557]" />
                        </span>
                        <span v-else>
                            <Icon name="check-circle" class="block h-6 w-6 fill-gray-400" />
                        </span>
                    </td>
                    <td class="px-6 py-4 border-t">{{ reminder.template === 'purple' ? 'Eliquis' : 'Pfizer' }}</td>
                    <td class="px-6 py-4 border-t">{{ reminder.send_date }}</td>
                    <td class="px-6 w-px border-t">
                        <ActionMenu :links="[
                            { text: 'Se statistik', href: `/reminders/${ reminder.id }/statistics`, disabled: !reminder.gcma_status },
                            { text: 'Redigera', href: `/reminders/${ reminder.id }/edit` },
                        ]" />
                    </td>
				</tr>

				<tr v-if="reminders.length === 0">
					<td class="border-t px-6 py-4" colspan="4">{{ __('No reminders found.') }}</td>
				</tr>
			</table>

            <Pagination v-if="reminders.length > 0" class="mt-6" :links="reminders.links" />
        </div>
    </div>
</template>

<script>
    import { Head, Link } from '@inertiajs/inertia-vue3'
	import pickBy 		  from 'lodash/pickBy'
	import throttle 	  from 'lodash/throttle'
	import mapValues 	  from 'lodash/mapValues'
    import Icon           from '@/Shared/Icon'
    import Layout         from '@/Shared/Layout'
    import Pagination     from '@/Shared/Pagination'
    import TextInput      from '@/Shared/TextInput'
    import SearchFilter   from '@/Shared/SearchFilter'
    import ActionMenu from '../../Shared/ActionMenu.vue'

    export default {
        components: {
            ActionMenu,
            Head,
            Icon,
            Link,
            Pagination,
            TextInput,
            SearchFilter
        },
        layout: Layout,
        props: {
            filters: Object,
            reminders: [Object, Array],
        },
        data() {
            return {
				form: {
					search: this.filters.search,
					trashed: this.filters.trashed,
				}
            }
        },
		watch: {
			form: {
				deep: true,
				handler: throttle(function () {
					this.$inertia.get('/reminders', pickBy(this.form), { preserveState: true })
				}, 150)
			}
		},
        methods: {
            capitalize(string) {
                return string.charAt(0).toUpperCase() + string.slice(1);
            },
			reset() {
				this.form = mapValues(this.form, () => null)
			},
            getFilterCriteria(reminder) {

                const sendDate = new Date(reminder.send_date);
                sendDate.setMonth(sendDate.getMonth() - reminder.last_order_months_ago);
                const sendDateFormatted = sendDate.toISOString().split('T')[0];

                let filterCriteria = `Kunder som inte har gjort någon beställning efter ${sendDateFormatted}`;

                const productsNames = reminder.products.map(product => product.name.charAt(0).toUpperCase() + product.name.slice(1));
                filterCriteria += productsNames.length ? `<p class="whitespace-normal">${productsNames.join(', ')}</p>` : `<p>Alla produkter</p>`;

                const regionsNames = reminder.regions.map(region => region.name.charAt(0).toUpperCase() + region.name.slice(1));
                filterCriteria += regionsNames.length ? `<p class="whitespace-normal">${regionsNames.join(', ')}</p>` : `<p>Alla regioner</p>`;

                const rolesNames = reminder.roles.map(role => role.name.charAt(0).toUpperCase() + role.name.slice(1));
                filterCriteria += rolesNames.length ? `<p class="whitespace-normal">${rolesNames.join(', ')}</p>` : `<p>Alla roller</p>`;

                return filterCriteria;
            }
        },
    }
</script>
